import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import Home from "./pages/Home";
import ExecutiveCvPage from "./pages/ExecutiveCvPage";
import PricingPage from "./pages/PricingPage";
import AiCvMakerPage from "./pages/AiCvMakerPage";
import ServicesCvWritingPage from "./pages/ServicesCvWritingPage";
import ServicesLinkedInPage from "./pages/ServicesLinkedInPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import CareerResourcesHub from "./pages/CareerResourcesHub";
import CareerResourceArticle from "./pages/CareerResourceArticle";
import Login from "./pages/Login";
import Register from "./pages/Register";
import EmmaChat from "./components/EmmaChat";
import WhatsAppLauncher from "./components/WhatsAppLauncher";
import ScrollToTop from "./components/ScrollToTop";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./lib/auth";

// Heavy app pages are code-split out of the initial bundle — they carry the
// PayPal SDK, the CV editor and the admin console, none of which marketing
// visitors need for first paint.
const Checkout = lazy(() => import("./pages/Checkout"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const Admin = lazy(() => import("./pages/Admin"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AiCvBuilder = lazy(() => import("./pages/AiCvBuilder"));
const MyCvs = lazy(() => import("./pages/MyCvs"));
const CvEditor = lazy(() => import("./pages/CvEditor"));
const MyOrders = lazy(() => import("./pages/MyOrders"));
const Profile = lazy(() => import("./pages/Profile"));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="h-8 w-8 rounded-full border-2 border-brand border-t-transparent animate-spin" />
    </div>
  );
}

function LegacyAiCvMakerRedirect() {
  // Preserve query params (?kw=, utm_*, gclid, etc.) when redirecting from the
  // legacy /ai-cv-maker path — needed for Google Ads DKI on the new URL.
  const { search, hash } = window.location;
  return <Navigate to={`/ai-cv-maker-builder${search}${hash}`} replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/ai-cv-maker-builder" element={<AiCvMakerPage />} />
            {/* Legacy redirect — /ai-cv-maker → /ai-cv-maker-builder (kept for old links & SEO) */}
            <Route path="/ai-cv-maker" element={<LegacyAiCvMakerRedirect />} />
            <Route path="/services/cv-writing" element={<ServicesCvWritingPage />} />
            <Route path="/services/linkedin-optimisation" element={<ServicesLinkedInPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/career-resources" element={<CareerResourcesHub />} />
            <Route path="/career-resources/:slug" element={<CareerResourceArticle />} />
            <Route path="/executive-cv-writing-service" element={<ExecutiveCvPage />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/checkout/:packageId" element={<Checkout />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-cv-builder"
              element={
                <ProtectedRoute signupFirst>
                  <AiCvBuilder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-cvs"
              element={
                <ProtectedRoute>
                  <MyCvs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-cvs/:cvId"
              element={
                <ProtectedRoute>
                  <CvEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-orders"
              element={
                <ProtectedRoute>
                  <MyOrders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Home />} />
          </Routes>
          </Suspense>
          {/* Global floating WhatsApp launcher. Sarah's chat panel is
              still available on demand via `sarah:open` (e.g. the "Ask
              Sarah" button on the AI CV Maker page). */}
          <WhatsAppLauncher />
          <EmmaChat />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
